import { Divider } from '@heroui/react';
import React from 'react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormClearErrors,
} from 'react-hook-form';

import {
  ChocoCheckbox,
  PhoneInput,
} from '../../../../components/FormComponents';
import { UserFormData } from '../UserForm/hooks/useUserFormValidation';

interface OrderPreferencesFieldProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  setValue: UseFormSetValue<UserFormData>;
  clearErrors: UseFormClearErrors<UserFormData>;
  disabled?: boolean;
}

export const OrderPreferencesField: React.FC<OrderPreferencesFieldProps> = ({
  control,
  errors,
  watch,
  setValue,
  clearErrors,
  disabled = false,
}) => {
  // Watch country codes for validation
  const whatsAppCountryCode = watch('whatsAppCountryCode') || 'US';
  const smsCountryCode = watch('smsCountryCode') || 'US';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-foreground">
          Order Preferences
        </h3>
        <p className="text-sm text-default-500 mt-1">
          Configure how this user receives order notifications and documents
          (optional).
        </p>
      </div>

      <div className="p-4 bg-default-50 border border-default-200 rounded-lg space-y-4">
        {/* Document Preferences */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">
            Document Delivery
          </h4>
          <div className="flex flex-col gap-6">
            <Controller
              name="orderPreferences.pdf"
              control={control}
              render={({ field }) => (
                <ChocoCheckbox
                  isSelected={field.value || false}
                  onValueChange={field.onChange}
                  isDisabled={disabled}
                  size="sm"
                  label="Save PDF"
                  description="Save order documents as PDF files"
                />
              )}
            />

            <Controller
              name="orderPreferences.xlsx"
              control={control}
              render={({ field }) => (
                <ChocoCheckbox
                  isSelected={field.value || false}
                  onValueChange={field.onChange}
                  isDisabled={disabled}
                  size="sm"
                  label="Attach XLSX"
                  description="Include order details in Excel format"
                />
              )}
            />
          </div>
        </div>

        <Divider />

        {/* Notification Preferences */}
        <div>
          <h4 className="text-sm font-medium text-foreground mb-3">
            Notification Delivery
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="orderPreferences.whatsApp"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  id="order-whatsapp"
                  name={field.name}
                  label="WhatsApp Number"
                  value={field.value || ''}
                  placeholder="Enter WhatsApp number"
                  countryCode={whatsAppCountryCode}
                  onChangeCountryCode={(countryCode) => {
                    setValue('whatsAppCountryCode', countryCode);
                  }}
                  onChange={(phoneNumber) => {
                    // Clear errors immediately for international numbers to prevent flickering
                    if (phoneNumber.startsWith('+')) {
                      clearErrors('orderPreferences.whatsApp');
                    }
                    field.onChange(phoneNumber);
                  }}
                  isInvalid={!!errors.orderPreferences?.whatsApp}
                  errorMessage={errors.orderPreferences?.whatsApp?.message}
                  helperText="Phone number to receive WhatsApp notifications"
                  data-testid="order-whatsapp-input"
                  disabled={disabled}
                />
              )}
            />

            <Controller
              name="orderPreferences.sms"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  id="order-sms"
                  name={field.name}
                  label="SMS Number"
                  value={field.value || ''}
                  placeholder="Enter SMS number"
                  countryCode={smsCountryCode}
                  onChangeCountryCode={(countryCode) => {
                    setValue('smsCountryCode', countryCode);
                  }}
                  onChange={(phoneNumber) => {
                    // Clear errors immediately for international numbers to prevent flickering
                    if (phoneNumber.startsWith('+')) {
                      clearErrors('orderPreferences.sms');
                    }
                    field.onChange(phoneNumber);
                  }}
                  isInvalid={!!errors.orderPreferences?.sms}
                  errorMessage={errors.orderPreferences?.sms?.message}
                  helperText="Phone number to receive SMS notifications"
                  data-testid="order-sms-input"
                  disabled={disabled}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
